<template>
  <!-- Mobile Slide-up Modal -->
  <div v-if="isMobile && isVisible" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end">
    <div class="w-full bg-[#ABBCF4] rounded-t-3xl transform transition-transform duration-300 ease-out max-h-[90vh] flex flex-col"
         :class="isVisible ? 'translate-y-0' : 'translate-y-full'">

      <!-- Mobile Content -->
      <div class="flex-1 overflow-y-auto">

      <!-- Search Form (shown when not showing results) -->
      <div v-if="!showResults" class="h-full flex flex-col">
        <!-- Header Section with Close Button -->
        <div class="px-6 py-6 pt-4 relative">
          <!-- Close Button -->
          <button
            @click="close"
            class="absolute top-4 right-4 w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all z-10"
          >
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
          <div class="flex items-center mb-6">
            <h2 class="text-3xl font-bold text-white">Find Trains</h2>
          </div>
        </div>

        <!-- Train Logo Section -->
        <div class="flex-1 flex items-center justify-center px-6">
          <div class="text-center">
            <img src="/train.svg" alt="Train" class="h-24 w-24 mx-auto mb-4 opacity-80" />
            <div class="text-white text-opacity-70 text-lg font-medium">Search Indian Railways</div>
            <div class="text-white text-opacity-50 text-sm mt-2">Find trains across India</div>
          </div>
        </div>

        <!-- Search Inputs Section - Bottom -->
        <div class="px-6 pb-6">
          <!-- Error message -->
          <div v-if="error" class="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
            {{ error }}
          </div>

          <!-- From station input -->
          <div class="mb-2">
            <div class="relative">
              <input
                type="text"
                v-model="fromStation"
                @input="handleFromStationInput"
                @focus="handleFromStationFocus"
                @blur="hideFromSuggestions"
                placeholder="Enter departure station"
                class="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none bg-white"
                autocomplete="off"
              />
              <button class="absolute right-3 top-3 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
                </svg>
              </button>

              <!-- From Station Suggestions Dropdown -->
              <div
                v-if="showFromSuggestions && fromSuggestions.length > 0"
                class="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto mt-1"
              >
                <div
                  v-for="(suggestion, index) in fromSuggestions"
                  :key="index"
                  @mousedown="selectFromStation(suggestion)"
                  class="px-3 py-2 hover:bg-teal-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <span class="text-sm text-gray-800">{{ suggestion }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Swap stations button - positioned between inputs -->
          <div class="relative flex justify-center z-20">
            <button
              @click="swapStations"
              class="bg-teal-500 text-white rounded-full p-2 shadow-lg hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-opacity-50 border-2 border-white transition-all duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M7 16V4M7 4L3 8M7 4L11 8" />
                <path d="M17 8v12M17 20l4-4M17 20l-4-4" />
              </svg>
            </button>
          </div>

          <!-- To station input -->
          <div class="mb-6">
            <div class="relative">
              <input
                type="text"
                v-model="toStation"
                @input="handleToStationInput"
                @focus="handleToStationFocus"
                @blur="hideToSuggestions"
                placeholder="Enter destination station"
                class="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none bg-white"
                autocomplete="off"
              />
              <button class="absolute right-3 top-3 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
                </svg>
              </button>

              <!-- To Station Suggestions -->
              <div
                v-if="showToSuggestions && toSuggestions.length > 0"
                class="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto mt-1"
              >
                <div
                  v-for="(suggestion, index) in toSuggestions"
                  :key="index"
                  @mousedown="selectToStation(suggestion)"
                  class="px-3 py-2 hover:bg-teal-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <span class="text-sm text-gray-800">{{ suggestion }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Search button -->
          <button @click="searchTrains" :disabled="isLoading || !fromStation || !toStation" class="w-full bg-blue-500 text-white py-3 rounded-3xl font-medium hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed">
            <span v-if="isLoading" class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Searching...
            </span>
            <span v-else>Search Trains</span>
          </button>
        </div>
      </div>

      <!-- Results View (shown after search) -->
      <div v-else class="h-full">
        <!-- Header at the very top -->
        <div class="flex items-center justify-between px-4 py-4 bg-[#ABBCF4] sticky top-0 z-20 rounded-t-3xl">
          <button @click="showResults = false" class="text-white p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
            </svg>
          </button>
          <h2 class="text-xl font-bold text-white">Train Results</h2>
          <!-- Close Button -->
          <button
            @click="close"
            class="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
          >
            <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Train Results Component -->
        <div class="px-4 pb-4">
          <TrainResultsComponent
            :from="fromStation"
            :to="toStation"
            :trains="trainResults"
            :is-loading="isLoading"
            :error="error"
            @retry="showResults = false"
          />
        </div>
      </div>
      </div>
    </div>
  </div>

  <!-- Desktop/Inline Mode -->
  <div v-else-if="isInline || (!isMobile && isVisible)"
       class="h-full bg-[#ABBCF4] rounded-3xl overflow-hidden">
    <!-- Same content as mobile but without the slide-up wrapper -->
    <!-- Search Form (shown when not showing results) -->
    <div v-if="!showResults" class="h-full flex flex-col">
      <!-- Header Section -->
      <div class="px-6 py-6">
        <div class="flex items-center mb-6">
          <h2 class="text-3xl font-bold text-white">Find Trains</h2>
        </div>
      </div>

      <!-- Train Logo Section -->
      <div class="flex-1 flex items-center justify-center px-6">
        <div class="text-center">
          <img src="/train.svg" alt="Train" class="h-24 w-24 mx-auto mb-4 opacity-80" />
          <div class="text-white text-opacity-70 text-lg font-medium">Search Indian Railways</div>
          <div class="text-white text-opacity-50 text-sm mt-2">Find trains across India</div>
        </div>
      </div>

      <!-- Search Inputs Section - Bottom -->
      <div class="px-6 pb-6">
        <!-- Error message -->
        <div v-if="error" class="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
          {{ error }}
        </div>

        <!-- From station input -->
        <div class="mb-2">
          <div class="relative">
            <input
              type="text"
              v-model="fromStation"
              @input="handleFromStationInput"
              @focus="handleFromStationFocus"
              @blur="hideFromSuggestions"
              placeholder="Enter departure station"
              class="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none bg-white"
              autocomplete="off"
            />
            <button class="absolute right-3 top-3 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
              </svg>
            </button>

            <!-- From Station Suggestions Dropdown -->
            <div
              v-if="showFromSuggestions && fromSuggestions.length > 0"
              class="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto mt-1"
            >
              <div
                v-for="(suggestion, index) in fromSuggestions"
                :key="index"
                @mousedown="selectFromStation(suggestion)"
                class="px-3 py-2 hover:bg-teal-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <span class="text-sm text-gray-800">{{ suggestion }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Swap stations button - positioned between inputs -->
        <div class="relative flex justify-center z-20">
          <button
            @click="swapStations"
            class="bg-teal-500 text-white rounded-full p-2 shadow-lg hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-opacity-50 border-2 border-white transition-all duration-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M7 16V4M7 4L3 8M7 4L11 8" />
              <path d="M17 8v12M17 20l4-4M17 20l-4-4" />
            </svg>
          </button>
        </div>

        <!-- To station input -->
        <div class="mb-6">
          <div class="relative">
            <input
              type="text"
              v-model="toStation"
              @input="handleToStationInput"
              @focus="handleToStationFocus"
              @blur="hideToSuggestions"
              placeholder="Enter destination station"
              class="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none bg-white"
              autocomplete="off"
            />
            <button class="absolute right-3 top-3 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
              </svg>
            </button>

            <!-- To Station Suggestions -->
            <div
              v-if="showToSuggestions && toSuggestions.length > 0"
              class="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto mt-1"
            >
              <div
                v-for="(suggestion, index) in toSuggestions"
                :key="index"
                @mousedown="selectToStation(suggestion)"
                class="px-3 py-2 hover:bg-teal-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <span class="text-sm text-gray-800">{{ suggestion }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Search button -->
        <button @click="searchTrains" :disabled="isLoading || !fromStation || !toStation" class="w-full bg-blue-500 text-white py-3 rounded-3xl font-medium hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed">
          <span v-if="isLoading" class="flex items-center justify-center">
            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Searching...
          </span>
          <span v-else>Search Trains</span>
        </button>
      </div>
    </div>

    <!-- Results View (shown after search) -->
    <div v-else class="px-4 py-4 h-full overflow-hidden flex flex-col">
      <div class="flex items-center justify-between mb-4">
        <button @click="showResults = false" class="text-teal-500 p-2 hover:bg-teal-50 rounded-lg transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
          </svg>
        </button>
        <h2 class="text-xl font-bold text-gray-800">Train Results</h2>
        <div class="w-9"></div> <!-- Spacer for alignment -->
      </div>

      <!-- Train Results Component -->
      <div class="flex-1 overflow-y-auto">
        <TrainResultsComponent
          :from="fromStation"
          :to="toStation"
          :trains="trainResults"
          :is-loading="isLoading"
          :error="error"
          @retry="showResults = false"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { searchTrains } from '@/services/trainService';
import TrainResultsComponent from '@/components/TrainResultsComponent.vue';
import { trainSuggestions } from '@/utils/suggestions';
import {
  getFilteredSuggestions,
  findBestMatch,
  getExactStationName
} from '@/utils/fuzzyMatch';
import { stationSwapMixin } from '@/mixins/stationSwapMixin';

export default {
  name: 'TrainSearchPopup',
  mixins: [stationSwapMixin],
  components: {
    TrainResultsComponent
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    isInline: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'search'],
  data() {
    return {
      fromStation: '',
      toStation: '',
      isLoading: false,
      error: null,
      showResults: false,
      trainResults: [],
      // Suggestion states
      fromSuggestions: [],
      toSuggestions: [],
      showFromSuggestions: false,
      showToSuggestions: false,
      // Debounce timers
      fromInputTimer: null,
      toInputTimer: null,
      // Exception mappings for special station name formatting
      stationExceptions: {
        'dum dum cantonment': 'dum-dum-cant'
        // Add more exceptions here as needed
        // 'station name': 'api-format'
      }
    }
  },
  methods: {
    close() {
      if (!this.isInline) {
        this.$emit('close');
        // Reset states when closing
        this.error = null;
        this.showResults = false;
        this.showFromSuggestions = false;
        this.showToSuggestions = false;
        this.fromSuggestions = [];
        this.toSuggestions = [];

        // Clear timers
        if (this.fromInputTimer) clearTimeout(this.fromInputTimer);
        if (this.toInputTimer) clearTimeout(this.toInputTimer);

        // Only reset train results when completely closing the popup
        this.trainResults = [];
      }
    },

    // Handle from station input with debouncing
    handleFromStationInput() {
      if (this.fromInputTimer) {
        clearTimeout(this.fromInputTimer);
      }

      this.fromInputTimer = setTimeout(() => {
        this.fromSuggestions = getFilteredSuggestions(this.fromStation, trainSuggestions, 3);
        this.showFromSuggestions = this.fromSuggestions.length > 0;
      }, 300);
    },

    // Handle from station focus
    handleFromStationFocus() {
      if (this.fromStation && this.fromStation.length >= 1) {
        this.fromSuggestions = getFilteredSuggestions(this.fromStation, trainSuggestions, 3);
        this.showFromSuggestions = this.fromSuggestions.length > 0;
      } else {
        this.showFromSuggestions = true;
        // Show some initial suggestions when focused with no input
        this.fromSuggestions = trainSuggestions.slice(0, 3);
      }
    },

    // Handle to station input with debouncing
    handleToStationInput() {
      if (this.toInputTimer) {
        clearTimeout(this.toInputTimer);
      }

      this.toInputTimer = setTimeout(() => {
        this.toSuggestions = getFilteredSuggestions(this.toStation, trainSuggestions, 3);
        this.showToSuggestions = this.toSuggestions.length > 0;
      }, 300);
    },

    // Handle to station focus
    handleToStationFocus() {
      if (this.toStation && this.toStation.length >= 1) {
        this.toSuggestions = getFilteredSuggestions(this.toStation, trainSuggestions, 3);
        this.showToSuggestions = this.toSuggestions.length > 0;
      } else {
        this.showToSuggestions = true;
        // Show some initial suggestions when focused with no input
        this.toSuggestions = trainSuggestions.slice(0, 3);
      }
    },

    // Select from station from suggestions
    selectFromStation(station) {
      this.fromStation = station;
      this.showFromSuggestions = false;
      this.fromSuggestions = [];
    },

    // Select to station from suggestions
    selectToStation(station) {
      this.toStation = station;
      this.showToSuggestions = false;
      this.toSuggestions = [];
    },

    // Hide from suggestions with delay to allow click
    hideFromSuggestions() {
      setTimeout(() => {
        this.showFromSuggestions = false;
      }, 200);
    },

    // Hide to suggestions with delay to allow click
    hideToSuggestions() {
      setTimeout(() => {
        this.showToSuggestions = false;
      }, 200);
    },
    async searchTrains() {
      if (!this.fromStation || !this.toStation) {
        this.error = 'Please enter both departure and destination stations.';
        return;
      }

      // Validate and get exact station names using fuzzy matching
      const validFromStation = this.getValidatedStation(this.fromStation);
      const validToStation = this.getValidatedStation(this.toStation);

      if (!validFromStation) {
        this.error = `"${this.fromStation}" is not a valid station. Please select from the suggestions.`;
        return;
      }

      if (!validToStation) {
        this.error = `"${this.toStation}" is not a valid station. Please select from the suggestions.`;
        return;
      }

      if (validFromStation.toLowerCase() === validToStation.toLowerCase()) {
        this.error = 'Departure and destination stations cannot be the same.';
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        // Format station names for API call (lowercase with hyphens)
        const formattedFromStation = this.formatStationForAPI(validFromStation);
        const formattedToStation = this.formatStationForAPI(validToStation);

        // Use the formatted station names for API call
        const result = await searchTrains(formattedFromStation, formattedToStation);

        // Store the results
        if (result && result.success && result.trains) {
          this.trainResults = result.trains;

          // Update the input fields with validated station names
          this.fromStation = validFromStation;
          this.toStation = validToStation;

          // Show the results view
          this.showResults = true;

          // Emit event with search parameters and results
          this.$emit('search', {
            from: validFromStation,
            to: validToStation,
            results: result
          });
        } else {
          this.trainResults = [];
          this.error = result.message || 'No trains found for this route.';
          this.showResults = true;
        }
      } catch (error) {
        // Handle CORS errors specifically
        if (error.message && error.message.includes('Failed to fetch')) {
          this.error = 'Network error: Could not connect to the train service. Please try again later or contact support.';
        } else {
          this.error = error.message || 'Failed to fetch train data. Please try again.';
        }

        this.trainResults = [];
        this.showResults = true;
      } finally {
        this.isLoading = false;
      }
    },

    // Validate station name and return exact match or best fuzzy match
    getValidatedStation(stationName) {
      if (!stationName) return null;

      // First try to get exact match
      const exactMatch = getExactStationName(stationName, trainSuggestions);
      if (exactMatch) return exactMatch;

      // If no exact match, try fuzzy matching
      const fuzzyMatch = findBestMatch(stationName, trainSuggestions, 3);
      return fuzzyMatch;
    },

    // Format station name for API call (lowercase with hyphens instead of spaces)
    formatStationForAPI(stationName) {
      if (!stationName) return '';

      const normalizedName = stationName.toLowerCase().trim();

      // Check if station has a special exception mapping
      if (this.stationExceptions[normalizedName]) {
        return this.stationExceptions[normalizedName];
      }

      // Default formatting: lowercase with hyphens
      return stationName
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')  // Replace one or more spaces with hyphens
        .replace(/[^\w-]/g, '') // Remove special characters except hyphens and word characters
        .replace(/-+/g, '-')    // Replace multiple consecutive hyphens with single hyphen
        .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
    }
  }
}
</script>

<style scoped>
/* Hide scrollbar while maintaining scroll functionality */
.hide-scrollbar {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.hide-scrollbar::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}
</style>
